import type { ReactNode } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage, useIntl } from 'react-intl';
import type { To } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import styled from 'styled-components';

import { Link, catchAll } from '@sorare/routing';

import { IconButton } from '#src/atoms/buttons/IconButton/index.tsx';
import { iconChevronLeft } from '#src/atoms/icons/system/iconChevronLeft.ts';
import { iconX } from '#src/atoms/icons/system/iconX.ts';
import { Portal } from '#src/atoms/layout/Portal/index.tsx';
import { Horizontal } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineL } from '#src/atoms/typography/index.tsx';
import { UserCurrencies } from '#src/components/navigation/AppNavigation/UserCurrencies/index.tsx';
import { UserNav } from '#src/components/navigation/UserNav/index.tsx';
import { ANY_SPORT_MARKET } from '#src/constants/__generated__/routes.ts';
import { useSportContext } from '#src/contexts/sport/index.ts';
import { useIsLoggedIn } from '#src/hooks/auth/useIsLoggedIn.ts';
import { useEnableSetFeatures } from '#src/hooks/commonGame/useEnableSetFeatures.ts';
import { useSeriesNav } from '#src/hooks/commonGame/useSeriesNav.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useDeeplinkFromNativeApp } from '#src/hooks/useDeeplinkFromNativeApp.ts';
import { useIsMobileApp } from '#src/hooks/useIsMobileApp.ts';
import { glossary } from '#src/lib/glossary.ts';
import { lazy } from '#src/lib/retry.ts';
import { hasPreviousLocation } from '#src/lib/routing.tsx';
import { matchAnySportPath } from '#src/lib/routing/generateSportPath.ts';

import { CompactBalances } from '../AppNavigation/CompactBalances';
import { ProSetToggle } from '../AppNavigation/ProSetToggle';

const NavigationSorareWheel = lazy(
  async () =>
    import('@sorare/marketplace/src/components/Wheel/NavigationSorareWheel'),
  'NavigationSorareWheel'
);

const StyledLink = styled(Link)`
  overflow: hidden;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
  }
`;

const Title = styled(HeadlineL)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

type Props = {
  text?: MessageDescriptor | string;
  children?: ReactNode;
  backTo?: To;
  disableBackBrowser?: boolean;
  onClick?: () => void;
  customTitle?: ReactNode;
};
export const PageHeader = ({
  text,
  children,
  backTo,
  onClick,
  disableBackBrowser,
  customTitle,
}: Props) => {
  const enableSetFeatures = useEnableSetFeatures();
  const seriesNav = useSeriesNav();
  const { pathname } = useLocation();
  const { sport } = useSportContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isIosApp } = useIsMobileApp();
  const isLaptopAndAbove = useIsLaptop();
  const isLoggedIn = useIsLoggedIn();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const location = useLocation();

  if (!isLoggedIn) {
    return null;
  }

  // back browser can be disabled using prop or
  // is always disabled if user comes from global NavItem
  const shouldDisableBackBrowser =
    disableBackBrowser || location.state?.fromNavItem;

  const backButton =
    backTo ||
    (!shouldDisableBackBrowser && hasPreviousLocation(-1)) ||
    onClick ? (
      <IconButton
        size="medium"
        color="transparent"
        onClick={() => {
          if (onClick) {
            onClick();
          } else if (backTo) {
            navigate(backTo);
          } else {
            navigate(-1);
          }
        }}
        icon={iconChevronLeft}
        aria-label={formatMessage(glossary.back)}
      />
    ) : (
      <>
        {!!isIosApp && (
          <IconButton
            size="medium"
            color="transparent"
            onClick={() => {
              navigateWithDeeplink();
            }}
            icon={iconX}
            aria-label={formatMessage(glossary.close)}
          />
        )}
      </>
    );

  const title = !!text && (
    <StyledLink
      to="."
      onClick={e => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }}
    >
      <Title as="p">
        {typeof text === 'string' ? text : <FormattedMessage {...text} />}
      </Title>
    </StyledLink>
  );

  const displayUserProfile = shouldDisableBackBrowser && !isLaptopAndAbove;

  const isProMarket =
    !!matchAnySportPath(catchAll(ANY_SPORT_MARKET), pathname) && !seriesNav;

  return (
    <>
      {!!displayUserProfile && !!enableSetFeatures && !isIosApp && (
        <Portal id="page-header-centered-item">
          <ProSetToggle />
        </Portal>
      )}
      <Portal id="page-header-title">
        {displayUserProfile ? (
          <Horizontal gap={0.5}>
            <UserNav
              sport={sport}
              size={isLaptopAndAbove ? 'medium' : 'small'}
            />
            <Horizontal gap={0.5}>
              {isProMarket ? (
                <CompactBalances />
              ) : (
                <UserCurrencies variant="vertical" />
              )}
              <NavigationSorareWheel />
            </Horizontal>
          </Horizontal>
        ) : (
          children || (
            <Horizontal>
              {backButton}
              {title}
              {customTitle}
            </Horizontal>
          )
        )}
      </Portal>
    </>
  );
};
